import React from 'react';
import Utilities from '@util/Utilities';
import { useFieldContext } from '@app/site/_form';
import { FieldErrors } from '@app/site/_form/field/FieldErrors';
import { MinusIcon, PlusIcon } from '@heroicons/react/20/solid';


type NumberFieldProps = {
	placeholder?: string,
	label?: string,
	isLoading?: boolean,
	disabled?: boolean,
	extraClassName?: string,
	labelHint?: string,
	hideErrorMessage?: boolean,
	min?: number,
	max?: number,
	step?: number,
	showControls?: boolean,

}

export const NumberField: React.FunctionComponent<NumberFieldProps> = (props) => {
	const field = useFieldContext<number | undefined>();
	const [inputValue, setInputValue] = React.useState<string>(field.state.value === undefined ? '' : `${field.state.value}`);

	const hasError = field.state.meta.isTouched && !field.state.meta.isValid;

	React.useEffect(() => {
		setInputValue(field.state.value === undefined ? '' : `${field.state.value}`);
	}, [field.state.value]);

	const isPartialNumber = (value: string) => {
		return value === '-' || value === '.' || value === '-.' || value === '+' || value === '+.';
	};

	const renderField = () => {
		return <div className="mt-0.5 flex rounded-md shadow-sm">
			{props.showControls && <button type="button"
																		 className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 px-3 text-slate-500 sm:text-sm focus:bg-blue-50"
																		 onClick={decrease}>
				<MinusIcon className="h-5 w-5 text-slate-400" />
			</button>}
			<input type="text"
						 inputMode={(props.step ?? 1) % 1 === 0 ? 'numeric' : 'decimal'}
						 className={Utilities.classNames(
							 'input',
							 props.showControls?"input-no-rounded": "",
							 Utilities.NVL(hasError, 'input-error'),
							 Utilities.NVL(props.isLoading, 'input-isLoading'),
							 Utilities.NVL(props.disabled, 'input-disabled'),
							 Utilities.NVL(!!props.extraClassName, props.extraClassName!),
						 )}
						 disabled={props.disabled}
						 placeholder={props.placeholder}
						 value={inputValue}
						 onChange={(e) => {
							 const nextInputValue = e.target.value;
							 setInputValue(nextInputValue);

							 if (nextInputValue === '') {
								 field.handleChange(undefined);
								 return;
							 }

							 if (isPartialNumber(nextInputValue)) {
								 return;
							 }

							 const value = Number(nextInputValue);
							 if (!Number.isNaN(value)) {
								 field.handleChange(value);
							 }
						 }}
						 onBlur={() => {
							 if (inputValue === '' || isPartialNumber(inputValue)) {
								 setInputValue(field.state.value === undefined ? '' : `${field.state.value}`);
								 return;
							 }

							 const value = Number(inputValue);
							 if (Number.isNaN(value)) {
								 setInputValue(field.state.value === undefined ? '' : `${field.state.value}`);
							 }
						 }} />
			{props.showControls && <button type="button"
																		 className="inline-flex items-center rounded-r-md border border-l-0 border-slate-300 px-3 text-slate-500 sm:text-sm focus:bg-blue-50"
																		 onClick={increase}>
				<PlusIcon className="h-5 w-5 text-slate-400" />
			</button>}
		</div>;
	};

	const decrease = () => {
		const value = field.state.value;
		if (value === undefined) return;
		const step = props.step ?? 1;
		const min = props.min ?? Number.NEGATIVE_INFINITY;
		if (value > min) {
			field.setValue(value - step);
		}
	};

	const increase = () => {
		const value = field.state.value;
		if (value === undefined) return;
		const step = props.step ?? 1;
		const max = props.max ?? Number.POSITIVE_INFINITY;
		if (value < max) {
			field.setValue(value + step);
		}
	};


	return !!props.label ? <div>
		<div className="flex">
			<div className="label">{props.label}</div>
			{!!props.labelHint ? <span className="text-xs leading-6 text-gray-500 ml-2 italic">{props.labelHint}</span> : ''}
		</div>
		{renderField()}
		{!props.hideErrorMessage && <FieldErrors meta={field.state.meta} />}
	</div> : renderField();
};
